import React from 'react'

import { Box, Typography, Tooltip } from '@mui/material'
import {
  SendOutlined,
  CheckCircleOutlineOutlined,
  InboxOutlined,
  ReplyOutlined,
  KeyboardArrowUp
} from '@mui/icons-material'

import Flag from '../../mailTemplate/src/components/atomic/atoms/Flag'
import McPageHelpers from '../../pages/mc-components/McPageHelpers'

export const STATUS_META = {
  DELIVERED: { color: '#10b981' },
  WAITING: { color: '#f59e0b' },
  FAILED: { color: '#ef4444' }
}

export const formatDate = (timestamp) => {
  if (!timestamp) return null

  const userLocale = McPageHelpers.localeMap[localStorage.getItem('lang')] || 'en-US'
  return new Date(timestamp).toLocaleString(userLocale).split(',')[0].split(' ')[0]
}

export const HsCell = ({ hsArr, hsCodesGSD }) => {
  if (!hsArr?.length) return null
  const primary = hsArr[0]
  const primaryLabel = hsCodesGSD?.find?.((x) => x.value === primary)?.label
  const restCount = hsArr.length - 1
  const restTooltip = restCount > 0
    ? hsArr.slice(1).map((code) => {
      const lbl = hsCodesGSD?.find?.((x) => x.value === code)?.label
      return lbl ? `${code} – ${lbl}` : code
    }).join('\n')
    : ''

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 0.875,
            height: 22,
            borderRadius: 1,
            background: 'linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)',
            border: '1px solid rgba(15, 23, 42, 0.06)',
            color: '#334155',
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: 0.6,
            fontFamily: '"SF Mono", Monaco, "Cascadia Mono", Consolas, monospace',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
            flexShrink: 0
          }}
        >
          {primary}
        </Box>
        {restCount > 0 && (
          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{restTooltip}</Box>} arrow placement="top-start">
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 22,
                px: 0.75,
                borderRadius: 1,
                backgroundColor: 'rgba(13, 148, 136, 0.08)',
                color: '#0d9488',
                fontSize: '0.65rem',
                fontWeight: 600,
                lineHeight: 1,
                flexShrink: 0,
                transition: 'background-color 0.18s ease',
                '&:hover': { backgroundColor: 'rgba(13, 148, 136, 0.14)' }
              }}
            >
              +{restCount}
            </Box>
          </Tooltip>
        )}
      </Box>
      {primaryLabel && (
        <Tooltip title={primaryLabel} arrow placement="top-start">
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 400,
              color: 'text.secondary',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: 'fit-content',
              maxWidth: '100%',
              transition: 'color 0.18s ease',
              '&:hover': { color: 'text.primary' }
            }}
          >
            {primaryLabel}
          </Typography>
        </Tooltip>
      )}
    </Box>
  )
}

export const CountryCell = ({ code, name }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '5px',
          boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06)',
          pointerEvents: 'none'
        }
      }}
    >
      <Flag
        code={code}
        square
        style={{
          fontSize: '1.25rem',
          borderRadius: '5px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          display: 'block'
        }}
      />
    </Box>
    <Typography
      sx={{
        fontSize: '0.78rem',
        fontWeight: 500,
        color: 'text.primary',
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {name || code}
    </Typography>
  </Box>
)

const StackedStatusBar = ({ emailStats, statusLabels }) => {
  const values = ['DELIVERED', 'WAITING', 'FAILED'].map((k) => ({
    key: k,
    count: emailStats?.[k] || 0
  }))

  const total = values.reduce((a, b) => a + b.count, 0)

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 8,
          borderRadius: 999,
          overflow: 'hidden',
          backgroundColor: '#f3f4f6',
          gap: total > 0 ? '2px' : 0
        }}
      >
        {total > 0
          ? values.filter((v) => v.count > 0).map((v) => (
            <Tooltip
              key={v.key}
              title={`${statusLabels[v.key] || v.key} · ${v.count.toLocaleString()}`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  width: `${(v.count / total) * 100}%`,
                  backgroundColor: STATUS_META[v.key].color,
                  transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              />
            </Tooltip>
          ))
          : null}
      </Box>
    </Box>
  )
}

const StatTile = ({ icon: Icon, value, label, color, accentBg, tooltip }) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.875,
        py: 0.625,
        borderRadius: 1.5,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: accentBg,
          '& .stat-icon': {
            transform: 'scale(1.08)',
            boxShadow: `0 2px 6px ${accentBg}`
          }
        }
      }}
    >
      <Box
        className="stat-icon"
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          backgroundColor: accentBg,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.2s ease'
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 400,
            color: 'text.primary',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.62rem',
            fontWeight: 400,
            color: 'text.secondary',
            lineHeight: 1,
            letterSpacing: 0.3
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  </Tooltip>
)

const HeroReplyTile = ({ replied, replyPercent, locale, label, fmt }) => {
  const hasReplies = replied > 0
  const hasPercent = !!replyPercent
  const pctDisplay = locale === 'tr' ? `%${replyPercent || 0}` : `${replyPercent || 0}%`

  return (
    <Tooltip title={`${label}${hasPercent ? ` · ${pctDisplay}` : ''}`} arrow placement="top">
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.875,
          py: 0.625,
          borderRadius: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(13, 148, 136, 0.1)',
            '& .reply-icon': {
              transform: 'scale(1.08)',
              boxShadow: '0 2px 6px rgba(13, 148, 136, 0.18)'
            }
          }
        }}
      >
        <Box
          className="reply-icon"
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            backgroundColor: 'rgba(13, 148, 136, 0.1)',
            color: '#0d9488',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s ease'
          }}
        >
          <ReplyOutlined sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 400,
                color: 'text.primary',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {fmt(replied)}
            </Typography>
            {hasReplies && hasPercent && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: '#047857',
                  fontSize: '0.6rem',
                  fontWeight: 500,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  px: 0.5,
                  py: 0.125,
                  borderRadius: 0.75
                }}
              >
                <KeyboardArrowUp sx={{ fontSize: 11, ml: -0.25 }} />
                {pctDisplay}
              </Box>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: '0.62rem',
              fontWeight: 400,
              color: 'text.secondary',
              lineHeight: 1,
              letterSpacing: 0.3
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  )
}

export const EngagementCell = ({
  campaignReport,
  repliedLabel,
  replyPercent,
  locale,
  statusLabels,
  sentLabel,
  repliedShortLabel,
  deliveredShortLabel,
  incomingShortLabel
}) => {
  const stats = campaignReport?.outgoingEmailStats
  const incoming = campaignReport?.incomingEmailCount || 0
  const replied = campaignReport?.repliedEmailCount || 0

  const delivered = stats?.DELIVERED || 0
  const waiting = stats?.WAITING || 0
  const failed = stats?.FAILED || 0
  const totalSent = delivered + waiting + failed

  const fmt = (n) => n.toLocaleString()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <StatTile
          icon={SendOutlined}
          value={fmt(totalSent)}
          label={sentLabel}
          color="#2563eb"
          accentBg="rgba(37, 99, 235, 0.1)"
          tooltip={`${sentLabel} · ${fmt(totalSent)}`}
        />
        <StatTile
          icon={CheckCircleOutlineOutlined}
          value={fmt(delivered)}
          label={deliveredShortLabel}
          color="#10b981"
          accentBg="rgba(16, 185, 129, 0.1)"
          tooltip={`${deliveredShortLabel} · ${fmt(delivered)}`}
        />
        <StatTile
          icon={InboxOutlined}
          value={fmt(incoming)}
          label={incomingShortLabel}
          color="#8b5cf6"
          accentBg="rgba(139, 92, 246, 0.1)"
          tooltip={`${incomingShortLabel} · ${fmt(incoming)}`}
        />
        <HeroReplyTile
          replied={replied}
          replyPercent={replyPercent}
          locale={locale}
          label={repliedShortLabel}
          fmt={fmt}
        />
      </Box>
      <Box sx={{ px: 1 }}>
        <StackedStatusBar emailStats={stats} statusLabels={statusLabels} />
      </Box>
    </Box>
  )
}
